/* Copyright (C) nexleader - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Written for nexleader <myipsat.com>, 2016-2026
 */

import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { catchError, of, switchMap, take, tap, EMPTY } from 'rxjs';

import { ArchetypeReport, ArchetypeReportService, ArchetypeReportPriceResponse } from '../../../resources/archetype-report.service';
import { ArchetypeReportDiscountService } from '../../../resources/archetype-report-discount.service';
import { ErrorHandlerService } from '../../../../../services/error-handler.service';
import { NexleaderStripeElementModalComponent } from '../../../../store/components/stripe-element-modal/stripe-element-modal.component';
import { NexleaderCurrencyComponent } from '../../../../store/components/currency/currency.component';

/**
 * Regenerate Archetype Report Modal.
 *
 * Steve 2026-06-11 item 2: when a user edits their IPSAT 1.0 self-authored
 * statements after the 2.0 report was generated, the dashboard surfaces a
 * regenerate banner. The banner opens this modal to refresh the 2.0 report
 * against their latest answers.
 *
 * Steve 2026-07-22: refreshing now carries a small price (default $5, the
 * per-LP "refresh" price — see LicensingPartner.pricing.archetypeReport).
 * The user self-pays by card via the shared Stripe element modal, mirroring
 * the purchase flow. An optional discount code can still zero or reduce it
 * (e.g. a coach comp), in which case the Stripe step is skipped.
 */
@Component({
  selector: 'app-regenerate-archetype-report-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, NexleaderCurrencyComponent],
  templateUrl: './regenerate-archetype-report-modal.component.html',
})
export class RegenerateArchetypeReportModalComponent implements OnInit {
  @Input() surveyResultId: string = '';
  @Output() onRegenerated = new EventEmitter<ArchetypeReport>();

  price: ArchetypeReportPriceResponse | null = null;
  discountCode: string = '';
  appliedDiscount: string | null = null;
  discountError: string | null = null;
  isLoadingPrice: boolean = false;
  isSubmitting: boolean = false;
  /**
   * Set true once payment is complete and the /regenerate request is in
   * flight. The backend re-runs the engine + AI personalization before
   * responding (~15–30s), so we show a generating-state UI meanwhile.
   */
  isGeneratingReport: boolean = false;
  errorMessage: string = '';

  constructor(
    public bsModalRef: BsModalRef,
    private archetypeReportService: ArchetypeReportService,
    private archetypeReportDiscountService: ArchetypeReportDiscountService,
    private errorHandler: ErrorHandlerService,
    private modalService: BsModalService,
  ) {}

  ngOnInit(): void {
    this.loadPrice();
  }

  loadPrice(): void {
    this.isLoadingPrice = true;
    this.archetypeReportService
      .calculatePrice({
        surveyResultId: this.surveyResultId,
        archetypeReportDiscountName: this.appliedDiscount || undefined,
        isRefresh: true,
      })
      .pipe(
        tap((response) => {
          this.price = response;
          this.isLoadingPrice = false;
        }),
        catchError((error) => {
          this.errorHandler.handleError(error);
          this.isLoadingPrice = false;
          return of(null);
        })
      )
      .subscribe();
  }

  applyDiscount(): void {
    if (!this.discountCode.trim()) {
      return;
    }

    this.discountError = null;
    this.archetypeReportDiscountService
      .getByName(this.discountCode)
      .pipe(
        tap((response) => {
          if (response) {
            this.appliedDiscount = this.discountCode;
            this.discountCode = '';
            this.loadPrice();
          } else {
            this.discountError = 'Invalid discount code';
          }
        }),
        catchError(() => {
          this.discountError = 'Error validating discount code';
          return of(null);
        })
      )
      .subscribe();
  }

  removeDiscount(): void {
    this.appliedDiscount = null;
    this.discountError = null;
    this.loadPrice();
  }

  submit(): void {
    this.errorMessage = '';
    if (!this.surveyResultId) {
      this.errorMessage = 'Missing survey result reference. Reload the page and try again.';
      return;
    }
    if (!this.price || this.isSubmitting) {
      return;
    }

    this.isSubmitting = true;

    // Discount (or a $0 refresh price) covers it — no card needed.
    if (this.price.currencyQuantity <= 0) {
      this.completeRegenerate(null).subscribe();
      return;
    }

    // Collect the refresh fee via the shared Stripe element modal.
    const stripeModalRef = this.modalService.show(
      NexleaderStripeElementModalComponent,
      {
        initialState: {
          title: 'Refresh Your IPSAT 2.0 Report',
          description: 'Regenerate your report against your updated statements',
          currencyCode: this.price.currencyCode?.toLowerCase() || 'usd',
          amount: Math.trunc(this.price.currencyQuantity * 100),
        },
      }
    );

    stripeModalRef.onHide?.pipe(take(1)).subscribe(() => {
      this.isSubmitting = false;
    });

    stripeModalRef.content?.tokenGenerated
      .pipe(take(1))
      .pipe(
        tap(() => stripeModalRef.hide()),
        switchMap((token) => {
          if (!token) {
            this.isSubmitting = false;
            return EMPTY;
          }
          return this.completeRegenerate(token);
        })
      )
      .subscribe();
  }

  private completeRegenerate(stripeToken: any) {
    this.isGeneratingReport = true;
    return this.archetypeReportService
      .regenerate({
        surveyResultId: this.surveyResultId,
        discountCode: this.appliedDiscount || undefined,
        stripeToken: stripeToken,
      })
      .pipe(
        tap((report) => {
          this.isSubmitting = false;
          this.isGeneratingReport = false;
          this.onRegenerated.emit(report);
          this.bsModalRef.hide();
        }),
        catchError((err) => {
          this.isSubmitting = false;
          this.isGeneratingReport = false;
          this.errorMessage = (err?.error?.error) || err?.message || 'Unable to regenerate. Please try again.';
          return of(null);
        })
      );
  }

  close(): void {
    this.bsModalRef.hide();
  }
}
